import { apiClient } from './api';
import { Contact, CreateContactData, UpdateContactData, CreateInteractionData, PaginatedResponse, PaginationParams } from '../types';

export const contactsService = {
  // Get all contacts
  getContacts: async (params: PaginationParams = {}): Promise<PaginatedResponse<Contact>> => {
    const response = await apiClient.get('/api/contacts', { params });
    return {
      data: response.contacts,
      pagination: response.pagination
    };
  },

  // Get contact by ID
  getContact: async (id: string): Promise<Contact> => {
    const response = await apiClient.get(`/api/contacts/${id}`);
    return response.contact;
  },

  // Create new contact
  createContact: async (contactData: CreateContactData): Promise<Contact> => {
    const response = await apiClient.post('/api/contacts', contactData);
    return response.contact;
  },

  // Update contact
  updateContact: async (id: string, contactData: UpdateContactData): Promise<Contact> => {
    const response = await apiClient.put(`/api/contacts/${id}`, contactData);
    return response.contact;
  },

  // Delete contact
  deleteContact: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete(`/api/contacts/${id}`);
    return { message: response.message || 'Contact deleted successfully' };
  },

  // Get contacts by company
  getContactsByCompany: async (companyId: string, params: PaginationParams = {}): Promise<PaginatedResponse<Contact>> => {
    const response = await apiClient.get(`/api/contacts/company/${companyId}`, { params });
    return {
      data: response.contacts,
      pagination: response.pagination
    };
  },

  // Get contacts by relationship
  getContactsByRelationship: async (relationship: string, params: PaginationParams = {}): Promise<PaginatedResponse<Contact>> => {
    const response = await apiClient.get(`/api/contacts/relationship/${relationship}`, { params });
    return {
      data: response.contacts,
      pagination: response.pagination
    };
  },

  // Get contacts by priority
  getContactsByPriority: async (priority: string, params: PaginationParams = {}): Promise<PaginatedResponse<Contact>> => {
    const response = await apiClient.get(`/api/contacts/priority/${priority}`, { params });
    return {
      data: response.contacts,
      pagination: response.pagination
    };
  },

  // Get contacts with upcoming follow-ups
  getUpcomingFollowUps: async (params: PaginationParams = {}): Promise<Contact[]> => {
    const response = await apiClient.get('/api/contacts/followups/upcoming', { params });
    return response.contacts;
  },

  // Update contact follow-up
  updateContactFollowUp: async (id: string, followUpDate: string, notes?: string): Promise<Contact> => {
    const response = await apiClient.patch(`/api/contacts/${id}/followup`, { followUpDate, notes });
    return response.contact;
  },

  // Add note to contact
  addContactNote: async (id: string, note: string): Promise<Contact> => {
    const response = await apiClient.post(`/api/contacts/${id}/notes`, { note });
    return response.contact;
  },

  // Get contact statistics
  getContactStats: async (): Promise<any> => {
    const response = await apiClient.get('/api/contacts/stats/summary');
    return response.stats;
  },

  // Add interaction to contact
  addInteraction: async (contactId: string, interactionData: CreateInteractionData): Promise<Contact> => {
    const response = await apiClient.post(`/api/contacts/${contactId}/interactions`, interactionData);
    return response.contact;
  }
}; 